/**
 * StrikerBot - Medium mode striker AI.
 * Stays in opponent half (z <= 0), waits for pass, receives, attacks goal.
 */

import * as BABYLON from "babylonjs";
import { Ball } from "./Ball";
import { Player } from "./Player";
import { BotBase } from "./BotBase";
import { AIRole, AIState, AllowedZone, SeparationConfig } from "../systems/DifficultyManager";
import { KickType } from "./HumanoidMesh";

// Striker-specific constants
const GOAL_Z = -30;
const SHOOT_RANGE = 20;
const POSITIONING_RANGE = 5;

/**
 * Medium mode striker bot
 * - Zone restriction: opponent half only (z <= 0)
 * - States: WAIT, RECEIVE, ATTACK, SHOOT
 * - Behavior: Position for pass, receive, attack toward goal
 */
export class StrikerBot extends BotBase {
  private _targetPosition: BABYLON.Vector3 | null = null;

  constructor(
    scene: BABYLON.Scene,
    ball: Ball,
    homePosition: BABYLON.Vector3,
    allowedZone: AllowedZone,
    separationConfig: SeparationConfig,
    shadowGenerator?: BABYLON.ShadowGenerator,
    player?: Player
  ) {
    super(
      scene,
      ball,
      AIRole.STRIKER,
      homePosition,
      allowedZone,
      separationConfig,
      shadowGenerator,
      player,
      "strikerBot"
    );
  }

  /**
   * Light blue jersey color for striker (different from defender)
   */
  protected _getJerseyColor(): BABYLON.Color3 {
    return new BABYLON.Color3(0.2, 0.4, 0.9); // Light blue jersey
  }

  /**
   * Striker-specific decision making
   */
  protected _makeDecision(
    distToBall: number,
    distToGoal: number,
    ballPos: BABYLON.Vector3,
    myPos: BABYLON.Vector3
  ): void {
    // Check if ball is in our zone (opponent half)
    const ballInOurZone = ballPos.z <= 0;

    // If we have possession and are in shooting range
    if (this.hasBallPossession && distToGoal < SHOOT_RANGE && Math.abs(myPos.x) < 4) {
      this._state = AIState.SHOOT;
      return;
    }

    // If we have possession, attack toward goal
    if (this.hasBallPossession) {
      this._state = AIState.ATTACK;
      return;
    }

    // If ball is coming toward our zone (potential pass from defender)
    if (ballInOurZone && distToBall < 10) {
      this._state = AIState.RECEIVE;
      return;
    }

    // If ball is in our zone, chase it
    if (ballInOurZone) {
      this._state = AIState.CHASE_BALL;
      return;
    }

    // Otherwise wait and position for potential pass
    this._state = AIState.WAIT;
  }

  /**
   * Handle state behavior with striker-specific states
   */
  protected _handleState(
    ballPos: BABYLON.Vector3,
    myPos: BABYLON.Vector3,
    distToBall: number,
    distToGoal: number,
    dt: number
  ): void {
    switch (this._state) {
      case AIState.WAIT:
        this._handleWait(ballPos, myPos, dt);
        break;
      case AIState.RECEIVE:
        this._handleReceive(ballPos, myPos, distToBall, dt);
        break;
      case AIState.ATTACK:
        this._handleAttack(ballPos, myPos, distToGoal, dt);
        break;
      default:
        super._handleState(ballPos, myPos, distToBall, distToGoal, dt);
    }
  }

  /**
   * Wait and position for potential pass
   * Stay in good attacking position
   */
  private _handleWait(ballPos: BABYLON.Vector3, myPos: BABYLON.Vector3, dt: number): void {
    // Calculate ideal position - center of opponent half, ready for through ball
    const idealPos = new BABYLON.Vector3(
      -ballPos.x * 0.3, // Offset from ball's x position
      0,
      -15 // Center of opponent half
    );

    // Clamp to our zone
    idealPos.z = Math.min(idealPos.z, this._allowedZone.maxZ);
    idealPos.x = BABYLON.Scalar.Clamp(idealPos.x, this._allowedZone.minX, this._allowedZone.maxX);

    // Move toward ideal position
    const dx = idealPos.x - myPos.x;
    const dz = idealPos.z - myPos.z;
    const dist = Math.sqrt(dx * dx + dz * dz);

    if (dist > 1.0) {
      const speed = 5.5 * 0.5; // Slow positioning movement
      myPos.x += (dx / dist) * speed * dt;
      myPos.z += (dz / dist) * speed * dt;
      this._currentSpeed = speed;

      const angle = Math.atan2(dx, dz);
      this.mesh.rotation.y = BABYLON.Scalar.Lerp(this.mesh.rotation.y, angle, 0.1);
    } else {
      this._currentSpeed = 0;
      // Face the ball
      const toBall = ballPos.subtract(myPos);
      const angle = Math.atan2(toBall.x, toBall.z);
      this.mesh.rotation.y = BABYLON.Scalar.Lerp(this.mesh.rotation.y, angle, 0.1);
    }
  }

  /**
   * Receive ball behavior - move to intercept incoming ball
   */
  private _handleReceive(ballPos: BABYLON.Vector3, myPos: BABYLON.Vector3, distToBall: number, dt: number): void {
    // Predict ball trajectory and move to intercept
    const ballVel = this._ball.mesh.physicsImpostor?.getLinearVelocity();
    let targetPos: BABYLON.Vector3;

    if (ballVel && ballVel.length() > 2) {
      // Ball is moving - predict where to intercept
      const ballDir = new BABYLON.Vector3(ballVel.x, 0, ballVel.z).normalize();
      const timeToIntercept = distToBall / Math.max(ballVel.length(), 1);
      targetPos = ballPos.add(ballDir.scale(timeToIntercept * 2));
    } else {
      // Ball is slow - just go to ball
      targetPos = ballPos.clone();
    }

    // Clamp target to our zone
    targetPos.z = Math.min(targetPos.z, this._allowedZone.maxZ);
    targetPos.x = BABYLON.Scalar.Clamp(targetPos.x, this._allowedZone.minX, this._allowedZone.maxX);

    // Move toward target
    const dx = targetPos.x - myPos.x;
    const dz = targetPos.z - myPos.z;
    const dist = Math.sqrt(dx * dx + dz * dz);

    if (dist > 0.5) {
      const speed = dist > 5 ? 7.5 : 5.5; // Sprint if far
      myPos.x += (dx / dist) * speed * dt;
      myPos.z += (dz / dist) * speed * dt;
      this._currentSpeed = speed;

      const angle = Math.atan2(dx, dz);
      this.mesh.rotation.y = BABYLON.Scalar.Lerp(this.mesh.rotation.y, angle, 0.15);
    } else {
      this._currentSpeed = 0;
    }

    // If close enough to ball, gain possession and attack
    if (distToBall < 2.0) {
      this.hasBallPossession = true;
      this._state = AIState.ATTACK;
    }
  }

  /**
   * Attack behavior - dribble toward goal and shoot
   */
  private _handleAttack(ballPos: BABYLON.Vector3, myPos: BABYLON.Vector3, distToGoal: number, dt: number): void {
    // Direction toward goal
    const goalPos = new BABYLON.Vector3(0, 0, GOAL_Z);
    const toGoal = goalPos.subtract(myPos).normalize();

    // Dribble toward goal
    if (this._kickTimer <= 0) {
      // Small touch toward goal
      const dribbleDir = new BABYLON.Vector3(toGoal.x * 0.5, 0.1, toGoal.z * 0.5).normalize();
      this._ball.kick(dribbleDir, 3);
      this._kickTimer = 0.4;
      this.animator.triggerKick(KickType.Normal);
    }

    // Move toward ball to maintain control
    const dx = ballPos.x - myPos.x;
    const dz = ballPos.z - myPos.z;
    const dist = Math.sqrt(dx * dx + dz * dz);

    if (dist > 0.3) {
      myPos.x += (dx / dist) * 5.5 * dt;
      myPos.z += (dz / dist) * 5.5 * dt;
    }

    this._currentSpeed = 5.5;

    // Face toward goal
    const angle = Math.atan2(toGoal.x, toGoal.z);
    this.mesh.rotation.y = BABYLON.Scalar.Lerp(this.mesh.rotation.y, angle, 0.15);

    // If in shooting range, transition to shoot
    if (distToGoal < SHOOT_RANGE && Math.abs(myPos.x) < 4) {
      this._state = AIState.SHOOT;
    }
  }

  /**
   * Reset target position on position reset
   */
  resetPosition(): void {
    super.resetPosition();
    this._targetPosition = null;
  }
}